// Goal-celebration camera (D108) — the FIFA-style pan over the scorer while the ceremony runs.
//
// Pure and stateless: the scene keeps the clock and blends its normal follow cam toward this framing
// by `celebrationBlend`, so the camera eases in and back out instead of cutting. Presentation only
// (§13.5) — none of this is visible to the sim.
//
// The anchor is read straight off the entities' `action`: the sim already puts the celebrants into
// CELEBRATE, so the client needs neither the GOAL event nor a protocol change to know who to frame.

/** Total arc swept across the ceremony. A slow drift reads as a camera move, not a spin. */
export const CELEBRATION_SWEEP_RAD = 1.15;
/** Pulled back from the gameplay range (11.4) so a mobbed scorer fits in shot. */
const CELEBRATION_RANGE = 15.5;
/** Lower than gameplay: closer to pitch level makes the players loom, which is the point. */
const CELEBRATION_PITCH_RAD = 0.28;
const EASE_IN_END = 0.12; // fraction of the ceremony spent easing in
const EASE_OUT_START = 0.82; // …and where it starts handing back to the follow cam

export interface CelebrationFramingInput {
  /** 0 → 1 across the ceremony. */
  progress01: number;
  anchor: { x: number; z: number };
  /** The camera yaw right now, so the sweep starts where the player was already looking. */
  baseYaw: number;
}

export interface CelebrationFraming {
  yaw: number;
  pitch: number;
  range: number;
  anchor: { x: number; z: number };
}

/** Entity shape this module needs — a slice of ViewEntity, kept structural so tests stay cheap. */
interface AnchorEntity {
  action: string;
  pos: { x: number; y: number };
}

/**
 * What to frame: the celebrating players' centre. Falls back to the ball, which covers both the tick
 * before the CELEBRATE action lands and the case where the celebrant is off-screen or absent.
 */
export function celebrationAnchor(entities: readonly AnchorEntity[], ball: { x: number; z: number }): { x: number; z: number } {
  let x = 0;
  let z = 0;
  let n = 0;
  for (const e of entities) {
    if (e.action !== 'CELEBRATE') continue;
    x += e.pos.x;
    z += e.pos.y; // sim y is world z
    n += 1;
  }
  return n === 0 ? { x: ball.x, z: ball.z } : { x: x / n, z: z / n };
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (t: number): number => t * t * (3 - 2 * t); // smoothstep

/**
 * How much of the celebration framing to apply: 0 at both ends (normal follow cam), 1 through the
 * middle. Cutting straight to a cinematic camera and back is what makes replays feel cheap.
 */
export function celebrationBlend(progress01: number): number {
  const p = clamp01(progress01);
  if (p <= 0 || p >= 1) return 0;
  if (p < EASE_IN_END) return smooth(p / EASE_IN_END);
  if (p > EASE_OUT_START) return smooth((1 - p) / (1 - EASE_OUT_START));
  return 1;
}

/** The cinematic framing at this point in the ceremony. */
export function celebrationFraming({ progress01, anchor, baseYaw }: CelebrationFramingInput): CelebrationFraming {
  return {
    yaw: baseYaw + clamp01(progress01) * CELEBRATION_SWEEP_RAD,
    pitch: CELEBRATION_PITCH_RAD,
    range: CELEBRATION_RANGE,
    anchor,
  };
}
